import { Injectable } from '@angular/core';
import { FactSdk } from '../sdks/fact.sdk';
import { BehaviorSubject, combineLatest, forkJoin, map, Observable, of } from 'rxjs';
import { Fact } from '../models/ui/fact.model';
import { Collection } from '../models/ui/collection.model';
import { Issue } from '../models/ui/issue.model';
import { IssueSdk } from '../sdks/issue.sdk';
import { take } from 'rxjs/operators';
import { Guid } from '../models/guid';
import { flatMap, uniqBy } from 'lodash-es';
import { Custodian } from '../models/ui/custodian.model';
import { CustodianSdk } from '../sdks/custodian.sdk';
import { ReportingSdk } from '../sdks/reporting-sdk.service';
import { KnownReports } from '../constants/known-reports';
import { BuiltInFieldSdk } from '../sdks/built-in-field.sdk';
import { ExportSdk } from '../sdks/export.sdk';
import { ImportSdk } from '../sdks/import.sdk';
import { DateUtility } from '@utilities/date.utility';
import { cloneDeep } from 'lodash-es';
import { CustodianState } from "@models/ui/case-analytics-state/custodian-state.model";
import { ReportResponseResourceToCustodianStateMapper } from "@models/mappers/report-response-resource-to-custodian-state.mapper";
import { ReviewDocumentState } from '@models/ui/case-analytics-state/review-document-state.model';
import { ReportResponseResourceToResponsivenessReviewDocumentStateMapper } from '@models/mappers/report-response-resource-to-responsiveness-review-document-state.mapper';
import { ReportResponseResourceToReviewedDocumentCountMapper } from '@models/report-response-resource-to-reviewed-doc-count.mapper';
import { CollectionSummaryState } from '@models/ui/case-analytics-state/collection-summary-state.model';
import { ReportResponseResourceToCollectionSummaryStateMapper } from '@models/mappers/report-response-resource-to-collection-summary-state.mapper';
import { BuiltInFieldId } from '@models/built-in-field-id.enum';

/**
 * Manage responsible for stateful interactions with features in the analysis subsystem.
 */
@Injectable({
	providedIn: 'root'
})
export class AnalysisManager {
	public facts$: Observable<Collection<Fact>>;
	public issues$: Observable<Collection<Issue>>;
	public custodians$: Observable<Collection<Custodian>>;

	public filteredIssues$: Observable<Collection<Issue>>;
	public filteredFacts$: Observable<Collection<Fact>>;

	public selectedIssue$: Observable<Issue>;
	public selectedFact$: Observable<Fact>;
	public caseAssessmentFields$ = this.builtInFieldSdk.getCaseAssessmentFields();

	private factsSource: BehaviorSubject<Collection<Fact>> = new BehaviorSubject<Collection<Fact>>(null);
	private issuesSource: BehaviorSubject<Collection<Issue>> = new BehaviorSubject<Collection<Issue>>(null);
	private filteredIssuesSource: BehaviorSubject<Collection<Issue>> = new BehaviorSubject<Collection<Issue>>(null);
	private selectedIssueSource: BehaviorSubject<Issue> = new BehaviorSubject<Issue>(null);
	private selectedFactSource: BehaviorSubject<Fact> = new BehaviorSubject<Fact>(null);
	private currentFactAndIssueSearchTerm: string;
	private custodianCollectionSource: BehaviorSubject<Collection<Custodian>> = new BehaviorSubject<Collection<Custodian>>(null);

	constructor(private factSdk: FactSdk,
		private issueSdk: IssueSdk,
		private custodianSdk: CustodianSdk,
		private reportingSdk: ReportingSdk,
		private builtInFieldSdk: BuiltInFieldSdk,
		private exportSdk: ExportSdk,
		private importSdk: ImportSdk
	) {
		this.facts$ = this.factsSource.asObservable();
		this.issues$ = this.issuesSource.asObservable();
		this.custodians$ = this.custodianCollectionSource.asObservable();
		this.filteredIssues$ = this.filteredIssuesSource.asObservable();
		this.selectedIssue$ = this.selectedIssueSource.asObservable();
		this.selectedFact$ = this.selectedFactSource.asObservable();
		this.filteredFacts$ = combineLatest([this.filteredIssuesSource, this.selectedIssue$, this.facts$]).pipe(
			map(([collection, selectedIssue, allFacts]) => {
				let facts: Fact[];
				if (selectedIssue) {
					facts = flatMap(collection?.items, (item) => item?.facts);
					facts = facts.filter((fact) => {
						return fact.linkedIssues.some((issue) => issue.id === selectedIssue.id);
					});
				} else {
					facts = allFacts?.items ?? [];
				}

				facts = uniqBy(facts, (fact) => fact.id);
				facts = this.currentFactAndIssueSearchTerm ? facts.filter((fact) => {
					const normalizedTerm = this.currentFactAndIssueSearchTerm.toLowerCase();
					return fact.title?.toLowerCase().includes(normalizedTerm) ||
						fact.linkedIssues.some((issue) => {
							return issue.title.toLowerCase().includes(normalizedTerm) ||
								issue.description.toLowerCase().includes(normalizedTerm);
						});
				}) : facts;

				return new Collection<Fact>({ items: facts });
			})
		);
	}

	public refreshFacts(params?: RefreshFactsParams): void {
		this.factSdk.get({ id: params?.id }).subscribe((response) => this.factsSource.next(response));
	}

	public refreshCustodians(): void {
		this.custodianSdk.get().subscribe((response) => this.custodianCollectionSource.next(response));
	}

	public setSelectedFact(params: SetSelectedFactParams): void {
		if (params?.createNewFact) {
			this.selectedFactSource.next(new Fact());
		} else {
			this.factSdk.get({ id: params?.id }).subscribe((r) => {
				this.selectedFactSource.next(r.items[0]);
			});
		}
	}

	public saveFact(params?: SaveFactParams): Observable<Fact> {
		let clonedParams = null;
		if (params)
			clonedParams = cloneDeep(params);

		if (clonedParams?.fact?.dateStart) {
			clonedParams.fact.dateStart = DateUtility.formatDateTimeStringToUTC(clonedParams.fact.dateStart);
		}
		if (clonedParams?.fact?.dateEnd) {
			clonedParams.fact.dateEnd = DateUtility.formatDateTimeStringToUTC(clonedParams.fact.dateEnd);
		}

		if (clonedParams?.fact?.id === Guid.Empty) {
			return this.factSdk.post({ fact: clonedParams.fact });
		} else {
			return this.factSdk.put({ fact: clonedParams?.fact });
		}
	}

	public deleteFact(params?: DeleteFactParams): Observable<void> {
		if (params?.id) {
			return this.factSdk.delete({ id: params.id });
		}
	}

	public refreshIssues(params?: RefreshIssuesParams): void {
		this.issueSdk.get({
			id: params?.id
		}).subscribe((response) => {
			this.issuesSource.next(response);
		});
	}

	public refreshFilteredIssues(params?: RefreshFilteredIssuesParams): void {
		this.currentFactAndIssueSearchTerm = params?.searchTerm;
		this.issueSdk.get({
			searchTerm: params?.searchTerm
		}).subscribe((response) => this.filteredIssuesSource.next(response));
	}

	public setSelectedIssue(issue: Issue): void {
		this.filteredIssuesSource.pipe(take(1)).subscribe((r) => {
			const target = r.items.find((item) => item.id === issue?.id);
			if (target) {
				this.selectedIssueSource.next(target);
			} else {
				this.selectedIssueSource.next(null);
			}
		});
	}

	public saveIssue(params?: SaveIssueParams): Observable<Issue> {
		if (params?.issue?.id === Guid.Empty) {
			return this.issueSdk.post({ issue: params.issue });
		} else {
			return this.issueSdk.put({ issue: params?.issue });
		}
	}

	public deleteIssue(params?: DeleteIssueParams): Observable<void> {
		if (params?.issue?.id) {
			return this.issueSdk.delete({ issue: params.issue });
		}
	}

	public export(operation: AnalysisExportOperations): void {
		switch (operation) {
			case 'facts': {
				this.exportSdk.exportFacts().subscribe((url) => {
					window.open(url, '_blank');
				});
				break;
			}
			case 'facts-pivot': {
				this.exportSdk.exportFactsPivot().subscribe((url) => {
					window.open(url, '_blank');
				});
				break;
			}
		}
	}

	public import(operation: AnalysisImportOperations, data: any): Observable<void> {
		switch (operation) {
			case 'facts': {
				return this.importSdk.importFacts({ file: data as File });
			}
		}
		return of(null);
	}

	public getCaseAssessmentReport(reportType: KnownReports, filter: string, customReportParams?: CustomReportParams): Observable<{ pivotLabel: string, data: Record<string, string>[] }> {
		return this.reportingSdk.generateReportData({
			reportType,
			filter,
			customReportFields: {
				reportDateUnitType: customReportParams?.dateUnit,
				reportSumUnitType: customReportParams?.sumUnit,
				primaryDocField: customReportParams?.primaryFieldId,
				optionalDocField: customReportParams?.optionalFieldId
			}
		});
	}

	public getCustodianAnalyticsData(): Observable<CustodianState> {
		return this.reportingSdk.generateReportData({
			reportType: 'upload-doc-count-by-custodian',
			filter: null,
			customReportFields: {
				reportDateUnitType: null,
				reportSumUnitType:  null,
				primaryDocField: null,
				optionalDocField:  null,
			}
		}).pipe(
			map((x) => {
				const mapper = new ReportResponseResourceToCustodianStateMapper();
				return mapper.map(x)
			})
		);
	}

	public getCaseReviewDocumentAnalyticsData(): Observable<ReviewDocumentState> {
		const responsivenessReport$ = this.reportingSdk.generateReportData({
			reportType: 'review-doc-count-responsive-and-privileged-docs',
			filter: null,
			customReportFields: {
				reportDateUnitType: null,
				reportSumUnitType: null,
				primaryDocField: null,
				optionalDocField: null,
			}
		});
		const reviewReport$ = this.reportingSdk.generateReportData({
			reportType: 'review-status-docs',
			filter: null,
			customReportFields: {
				reportDateUnitType: null,
				reportSumUnitType: null,
				primaryDocField: null,
				optionalDocField: null,
			}
		});
		return forkJoin([responsivenessReport$, reviewReport$]).pipe(
			map(([report1Data, report2Data]) => {
				const mapper1 = new ReportResponseResourceToResponsivenessReviewDocumentStateMapper();
				const mapper2 = new ReportResponseResourceToReviewedDocumentCountMapper();
				const reviewDocumentState = mapper1.map(report1Data);
				reviewDocumentState.reviewedDocCount = mapper2.map(report2Data);
				return reviewDocumentState;
			})
		);
	}

	public getCollectionSummaryAnalyticsData(): Observable<CollectionSummaryState> {
		return this.reportingSdk.generateReportData({
			reportType: 'collection-summary' as KnownReports,
			filter: null,
			customReportFields: {
				reportDateUnitType: 'year-and-month',
				reportSumUnitType: 'doc-count',
				primaryDocField: BuiltInFieldId.MasterDate,
				optionalDocField: BuiltInFieldId.Custodian
			}
		}).pipe(
			map((x) => {
				const mapper = new ReportResponseResourceToCollectionSummaryStateMapper();
				return mapper.map(x)
			})
		);
	}

	public exportCaseAssessmentReport(reportType: KnownReports, filter: string, customReportParams?: CustomReportParams): void {
		this.exportSdk.exportReport({
			reportType,
			filter,
			customReportFields: {
				reportDateUnitType: customReportParams?.dateUnit,
				reportSumUnitType: customReportParams?.sumUnit,
				primaryDocField: customReportParams?.primaryFieldId,
				optionalDocField: customReportParams?.optionalFieldId
			}
		}).subscribe((url) => {
			window.open(url, '_blank');
		});
	}
}

interface RefreshFactsParams {
	id: string;
}

interface SaveFactParams {
	fact: Fact;
}

interface DeleteFactParams {
	id: string;
}

interface SetSelectedFactParams {
	id?: string;
	createNewFact?: boolean;
}

interface SaveIssueParams {
	issue: Issue;
}

interface RefreshIssuesParams {
	id: string;
}

interface RefreshFilteredIssuesParams {
	searchTerm: string;
}

interface DeleteIssueParams {
	issue: Issue;
}

interface CustomReportParams {
	sumUnit: string;
	dateUnit: string;
	primaryFieldId: string;
	optionalFieldId: string;
}

type AnalysisExportOperations = 'facts' | 'facts-pivot';
type AnalysisImportOperations = 'facts';
